import React, { useState, memo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Sitemap } from '../../components/Sitemap';
import { NextPage } from '../../components/NextPage';
import { BookIcon, VolumeIcon, CheckCircleIcon, PlayIcon, PauseIcon, LightbulbIcon, StarIcon, BrainIcon, PenIcon, HeadphonesIcon, ClockIcon, XIcon } from 'lucide-react';
const LESSON_DATA = {
  id: 3,
  title: {
    en: 'Numbers and Counting',
    ar: 'الأرقام والعد'
  },
  description: {
    en: 'Master numbers and basic counting',
    ar: 'أتقن الأرقام والعد الأساسي'
  },
  duration: '25 minutes',
  durationAr: '25 دقيقة',
  sections: [{
    id: 'basic-numbers',
    title: {
      en: 'Basic Numbers (1-10)',
      ar: 'الأرقام الأساسية (1-10)'
    },
    phrases: [{
      en: 'One',
      ar: 'واحد',
      context: 'I have one book',
      contextAr: 'لدي كتاب واحد'
    }, {
      en: 'Two',
      ar: 'اثنان',
      context: 'There are two cats',
      contextAr: 'هناك قطتان'
    }, {
      en: 'Three',
      ar: 'ثلاثة',
      context: 'Three students are absent',
      contextAr: 'ثلاثة طلاب غائبون'
    }, {
      en: 'Four',
      ar: 'أربعة',
      context: 'Four seasons in a year',
      contextAr: 'أربعة فصول في السنة'
    }, {
      en: 'Five',
      ar: 'خمسة',
      context: 'I have five fingers',
      contextAr: 'لدي خمسة أصابع'
    }]
  }, {
    id: 'counting-objects',
    title: {
      en: 'Counting Objects',
      ar: 'عد الأشياء'
    },
    phrases: [{
      en: 'One book',
      ar: 'كتاب واحد',
      context: 'I need one book',
      contextAr: 'أحتاج كتاباً واحداً'
    }, {
      en: 'Two cars',
      ar: 'سيارتان',
      context: 'There are two cars in the garage',
      contextAr: 'هناك سيارتان في المرآب'
    }, {
      en: 'Three apples',
      ar: 'ثلاث تفاحات',
      context: 'I ate three apples',
      contextAr: 'أكلت ثلاث تفاحات'
    }, {
      en: 'Four chairs',
      ar: 'أربعة كراسي',
      context: 'We need four chairs',
      contextAr: 'نحتاج أربعة كراسي'
    }]
  }],
  practice: [{
    question: {
      en: 'What comes after "four"?',
      ar: 'ماذا يأتي بعد "أربعة"؟'
    },
    options: [{
      en: 'Five',
      ar: 'خمسة',
      correct: true
    }, {
      en: 'Six',
      ar: 'ستة',
      correct: false
    }, {
      en: 'Three',
      ar: 'ثلاثة',
      correct: false
    }, {
      en: 'Seven',
      ar: 'سبعة',
      correct: false
    }]
  }, {
    question: {
      en: 'How do you say "3 books" in English?',
      ar: 'كيف تقول "3 كتب" باللغة الإنجليزية؟'
    },
    options: [{
      en: 'Three books',
      ar: 'ثلاثة كتب',
      correct: true
    }, {
      en: 'Three book',
      ar: 'ثلاثة كتاب',
      correct: false
    }, {
      en: 'Three a books',
      ar: 'ثلاثة أ كتب',
      correct: false
    }, {
      en: 'Threes books',
      ar: 'ثلاثات كتب',
      correct: false
    }]
  }],
  tips: [{
    en: 'Practice counting objects around you daily',
    ar: 'تدرب على عد الأشياء حولك يومياً'
  }, {
    en: 'Try to memorize numbers in sequence to build fluency',
    ar: 'حاول حفظ الأرقام بالتسلسل لبناء الطلاقة'
  }, {
    en: 'Use flashcards with numbers to improve recognition',
    ar: 'استخدم البطاقات التعليمية مع الأرقام لتحسين التعرف عليها'
  }]
};
export function NumbersCountingLesson() {
  const {
    isArabic
  } = useLanguage();
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const playAudio = (text: string) => {
    if (playingAudio === text) {
      window.speechSynthesis.cancel();
      setPlayingAudio(null);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.onend = () => setPlayingAudio(null);
    setPlayingAudio(text);
    window.speechSynthesis.speak(utterance);
  };
  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };
  const checkAnswers = () => {
    setShowResults(true);
  };
  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };
  const getCorrectAnswers = () => {
    return LESSON_DATA.practice.reduce((count, question, index) => {
      const selectedAnswer = selectedAnswers[index];
      if (selectedAnswer !== undefined && question.options[selectedAnswer].correct) {
        return count + 1;
      }
      return count;
    }, 0);
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3698">
      <Sitemap currentPage={{
      en: `Lesson ${LESSON_DATA.id}: ${LESSON_DATA.title.en}`,
      ar: `الدرس ${LESSON_DATA.id}: ${LESSON_DATA.title.ar}`
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: 'Course 1',
        ar: 'الدورة 1'
      },
      href: '/learn/course/1'
    }]} data-id="element-3699" />
      <div className="max-w-4xl mx-auto" data-id="element-3700">
        <div className="mb-12" data-id="element-3701">
          <div className="flex items-center gap-4 mb-6" data-id="element-3702">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3703">
              <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3704" />
            </div>
            <div data-id="element-3705">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3706">
                {isArabic ? LESSON_DATA.title.ar : LESSON_DATA.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-3707">
                {isArabic ? LESSON_DATA.description.ar : LESSON_DATA.description.en}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6" data-id="element-3708">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3709">
              <ClockIcon className="w-5 h-5" data-id="element-3710" />
              <span data-id="element-3711">
                {isArabic ? LESSON_DATA.durationAr : LESSON_DATA.duration}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3712">
              <BrainIcon className="w-5 h-5" data-id="element-3713" />
              <span data-id="element-3714">
                {LESSON_DATA.sections.length} {isArabic ? 'أقسام' : 'sections'}
              </span>
            </div>
          </div>
        </div>
        {LESSON_DATA.sections.map(section => <div key={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3715">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-3716">
              {isArabic ? section.title.ar : section.title.en}
            </h2>
            <div className="space-y-4" data-id="element-3717">
              {section.phrases.map((phrase, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-3718">
                  <div className="flex items-center justify-between mb-2" data-id="element-3719">
                    <div data-id="element-3720">
                      <div className="font-medium text-gray-900 dark:text-white text-lg" data-id="element-3721">
                        {phrase.en}
                      </div>
                      <div className="text-blue-600 dark:text-blue-400 text-lg" dir="rtl" data-id="element-3722">
                        {phrase.ar}
                      </div>
                    </div>
                    <button onClick={() => playAudio(phrase.en)} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-3723">
                      {playingAudio === phrase.en ? <PauseIcon className="w-5 h-5" data-id="element-3724" /> : <VolumeIcon className="w-5 h-5" data-id="element-3725" />}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3726">
                    {isArabic ? phrase.contextAr : phrase.context}
                  </div>
                </div>)}
            </div>
          </div>)}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3727">
          <div className="flex items-center gap-3 mb-6" data-id="element-3728">
            <PenIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3729" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3730">
              {isArabic ? 'تمارين' : 'Practice'}
            </h2>
          </div>
          <div className="space-y-8" data-id="element-3731">
            {LESSON_DATA.practice.map((question, qIndex) => <div key={qIndex} className="space-y-4" data-id="element-3732">
                <p className="text-lg text-gray-900 dark:text-white" data-id="element-3733">
                  {isArabic ? question.question.ar : question.question.en}
                </p>
                <div className="grid grid-cols-1 gap-3" data-id="element-3734">
                  {question.options.map((option, oIndex) => <button key={oIndex} onClick={() => handleAnswerSelect(qIndex, oIndex)} className={`
                        p-4 rounded-lg text-left transition-colors
                        ${selectedAnswers[qIndex] === oIndex ? showResults ? option.correct ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600'}
                      `} data-id="element-3735">
                      <div className="flex justify-between items-center" data-id="element-3736">
                        <div data-id="element-3737">
                          <div className="font-medium" data-id="element-3738">{option.en}</div>
                          <div className="text-sm opacity-75" data-id="element-3739">{option.ar}</div>
                        </div>
                        {showResults && (selectedAnswers[qIndex] === oIndex ? option.correct ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3740" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-3741" /> : null)}
                      </div>
                    </button>)}
                </div>
              </div>)}
          </div>
          <div className="mt-6" data-id="element-3742">
            {showResults ? <div data-id="element-3743">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-4" data-id="element-3744">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-3745">
                    {isArabic ? 'نتائجك' : 'Your Results'}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-3746">
                    {isArabic ? `لقد أجبت على ${getCorrectAnswers()} من ${LESSON_DATA.practice.length} بشكل صحيح` : `You got ${getCorrectAnswers()} out of ${LESSON_DATA.practice.length} correct`}
                  </p>
                </div>
                <button onClick={resetPractice} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3747">
                  {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
                </button>
              </div> : <button onClick={checkAnswers} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" disabled={Object.keys(selectedAnswers).length < LESSON_DATA.practice.length} data-id="element-3748">
                {isArabic ? 'تحقق من إجاباتك' : 'Check Answers'}
              </button>}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 mb-8" data-id="element-3749">
          <div className="flex items-center gap-3 mb-6" data-id="element-3750">
            <LightbulbIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3751" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3752">
              {isArabic ? 'نصائح مفيدة' : 'Helpful Tips'}
            </h2>
          </div>
          <ul className="space-y-4" data-id="element-3753">
            {LESSON_DATA.tips.map((tip, index) => <li key={index} className="flex items-start gap-3" data-id="element-3754">
                <StarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1" data-id="element-3755" />
                <span className="text-gray-800 dark:text-gray-200" data-id="element-3756">
                  {isArabic ? tip.ar : tip.en}
                </span>
              </li>)}
          </ul>
        </div>
        <NextPage currentPage={LESSON_DATA.id} totalPages={5} nextPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id + 1}`} prevPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id - 1}`} nextTitle={{
        en: 'Asking Questions',
        ar: 'طرح الأسئلة'
      }} nextDescription={{
        en: 'Learn how to form questions in English',
        ar: 'تعلم كيفية تكوين الأسئلة باللغة الإنجليزية'
      }} data-id="element-3757" />
      </div>
    </div>;
}